import React from 'react';
import { supabase } from '@/integrations/supabase/client';
import { normalizeAgeGroup } from '@/utils/ageUtils';
import { internalAuthService } from '@/services/internalAuthService';

export function useAuthActions() {
  // Session management utility
  const refreshSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      return session;
    } catch (error) {
      console.error('Session refresh failed:', error);
      throw new Error('Authentication session expired. Please log in again.');
    }
  };

  // Ensure user has valid session before performing operations
  const ensureValidSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session found. Please log in.');
      }
      
      // Check if session is close to expiry (within 5 minutes)
      const expiryTime = session.expires_at ? session.expires_at * 1000 : 0;
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;
      
      if (timeUntilExpiry < 5 * 60 * 1000) { // Less than 5 minutes
        console.log('🔄 Session expiring soon, refreshing...');
        return await refreshSession();
      }
      
      return session;
    } catch (error) {
      console.error('Session validation failed:', error);
      throw new Error('Authentication session expired. Please log in again.');
    }
  };

  const signUp = async (email: string, password: string, metadata: any) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      // Normalize age group before registration
      const normalizedMetadata = {
        ...metadata,
        age_group: metadata.age_group ? normalizeAgeGroup(metadata.age_group) : metadata.age_group
      };
      
      console.log('Supabase signup with normalized metadata:', normalizedMetadata);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: normalizedMetadata,
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        console.error('Supabase signup error:', error);
        return { success: false, error: error.message };
      }

      // CRITICAL: Clear internal auth session to prevent dual-session conflicts
      if (data.session) {
        try {
          console.log('AuthActions: Clearing internal session to prevent conflict');
          internalAuthService.logout();
          localStorage.removeItem('foundingAgentSession');
        } catch (clearError) {
          console.error('AuthActions: Failed to clear internal session (non-fatal):', clearError);
        }
      }

      console.log('Supabase signup successful:', data);
      return { 
        success: true, 
        needsEmailConfirmation: !data.session 
      };
    } catch (error) {
      console.error('Supabase signup exception:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Refresh session before sign in
      await supabase.auth.refreshSession();
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { success: false, error: error.message };
      }

      // CRITICAL: Clear internal auth session to prevent dual-session conflicts
      try {
        console.log('AuthActions: Clearing internal session to prevent conflict');
        internalAuthService.logout();
        localStorage.removeItem('foundingAgentSession');
      } catch (clearError) {
        console.error('AuthActions: Failed to clear internal session (non-fatal):', clearError);
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const updatePassword = async (password: string) => {
    try {
      // Ensure valid session before updating password
      await ensureValidSession();
      
      const { error } = await supabase.auth.updateUser({
        password
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const requestRoleUpgrade = async (role: string, reason: string) => {
    // TODO: Implement role upgrade request
    return { success: false, error: 'Not implemented' };
  };

  return {
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    requestRoleUpgrade,
    refreshSession,
    ensureValidSession
  };
}